// ============================================================================
// CANCUN100 — pdfUploader.js
// Helpers para generar PDFs en el navegador y subirlos a Supabase Storage.
// ============================================================================
// Estructura de buckets/paths:
//
//   pdfs-cliente/
//     ├── reservas/MT-1136.pdf
//     ├── reservas/MT-1136-1.pdf      (ajustes futuros)
//     └── cotizaciones/MTC-1108.pdf
//
//   pdfs-comision/
//     ├── reservas/MT-1136.pdf
//     └── cotizaciones/MTC-1108.pdf
//
// Buckets son PRIVADOS, se accede vía URLs firmadas (1 hora de vigencia).
// ============================================================================

import React from 'react';
import { etiquetaReglaAdmin } from '../reglasAdmin';
import { contactoPorCanal } from '../contacto';
import { partirNotas, sinEmoji } from '../notaVenta'; // APP 2/28 (RP-10)
import { extrasCupon, clasificar, etiquetasDe } from './cuponExtras'; // APP 2/30 (RP-11)
import { pdf } from '@react-pdf/renderer';
import PDFCliente from './PDFCliente';
import PDFClienteAnterior from './PDFClienteAnterior'; // APP 2/32c: conviven las dos versiones
import { CUPON_ENVIO, CUPON_DEFAULT, BUCKET_CLIENTE, rutaV2 } from './versionesCupon';
import { desgloseEdades } from '../edadesProducto'; // APP 2/38
import PDFComision from './PDFComision';
import PDFCambio from './PDFCambio';
import PDFPickups from './PDFPickups';
const PDFEnvio = CUPON_ENVIO === 'anterior' ? PDFClienteAnterior : PDFCliente; // lo que va en la ruta de siempre (correo / WhatsApp)

// ─── Helpers de formato ─────────────────────────────────────────────────
const fmtFechaCorta = (fecha) => {
  if (!fecha) return '';
  if (typeof fecha === 'string' && /^\d{4}-\d{2}-\d{2}/.test(fecha)) {
    const [y, m, d] = fecha.split('T')[0].split('-');
    return `${d}-${m}-${y}`;
  }
  return fecha;
};

const fmtFechaHora = (date) => {
  const d = date || new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mn = String(d.getMinutes()).padStart(2, '0');
  return `${dd}-${mm}-${yyyy} - ${hh}:${mn}`;
};

// ============================================================================
// sanitizeMetodoPago — limpia emojis y caracteres fuera del rango Latin1
// del método de pago para evitar que el font del PDF renderice basura
// (ej: "💵 Efectivo — EXENTO sin 5%" → "Efectivo — EXENTO sin 5%")
// ============================================================================
const sanitizeMetodoPago = (str) => {
  if (!str) return '—';
  // Remover emojis y caracteres fuera del rango imprimible/Latin extendido
  return String(str)
    .replace(/[\u{1F300}-\u{1FAFF}]/gu, '') // símbolos y emojis
    .replace(/[\u{2600}-\u{27BF}]/gu, '')   // símbolos varios (☀ ✓ etc)
    .replace(/[\u{1F000}-\u{1F2FF}]/gu, '') // otros símbolos
    .trim();
};

// ============================================================================
// buildDataCliente — construye el objeto `data` para PDFCliente
// ============================================================================
// Bloque 7: un producto temporal con proveedor "Otro" (sin código en `proveedores`) muestra el
// nombre capturado en su propio renglón. Cualquier otro producto: igual que siempre. No cambia montos.
const nombreProveedor = (product, prov) => {
  if (product.es_temporal && (!product.proveedor_codigo || product.proveedor_codigo === 'OTRO')) {
    return product.proveedor_nombre_tt || 'otro';
  }
  return prov?.nombre || product.proveedor_codigo;
};

export const buildDataCliente = ({ folio, body, catItems, customItems, proveedores, nombresIdioma = {}, extras = null }) => {
  // APP 2/30 (RP-11): tickets por servicio, traslados, códigos y QR para el cupón 2.0 (extras = cuponExtras.extrasCupon; opcional)
  const X = extras || { fotos: {}, etiquetas: {}, fichas: {}, tipos: {}, codigosHotel: [], qr: null };
  const folioNum = String(folio || '').replace(/\D/g, '') || '0';
  const esCotDoc = body.tipo_documento === 'cotizacion' || body.tipo === 'cotizacion';
  const idiomaDoc = ['es', 'en', 'pt'].includes(body.idioma_cliente) ? body.idioma_cliente : 'es';
  // APP 2/28 (RP-10): reservas.notas = texto del agente + «Importante:» (nota_venta por producto). Al PDF van separados y sin emoji.
  const notasPartidas = partirNotas(body.notas);
  const skuPorNombre = new Map(catItems.map((s) => [String(s.product?.nombre || '').trim().toLowerCase(), s.product?.sku]));
  const importante = notasPartidas.importante.map((x) => { const sku = skuPorNombre.get(String(x.nombre).toLowerCase()); return { nombre: sinEmoji((sku && nombresIdioma[sku]) || x.nombre), nota: sinEmoji(x.nota) }; });
  // Contabilidad v3 (B50): nombres de productos en el idioma del cliente (productos_web_meta.meta_title_en/pt) cuando existen.
  const nombreProd = (s) => (nombresIdioma && s.product && nombresIdioma[s.product.sku]) || s.product.nombre;
  // Custom hotel/vuelo: presentación aparte. Los "general" (o sin tipo = compat) van
  // en la tabla de servicios como siempre. Los precios NO cambian por esto.
  const tipoDe = (s) => s.tipo_custom || 'general';
  const customsGeneral = customItems.filter((s) => tipoDe(s) === 'general' || tipoDe(s) === 'transporte'); // B42: transporte va en la tabla
  const hospedaje = customItems.filter((s) => tipoDe(s) === 'hotel').map((s) => ({
    nombre: s.nombre || '—', hotel_codigo_confirmacion: s.hotel_codigo_confirmacion || s.codigo_confirmacion || '',
    fotos: (Array.isArray(s.hotel_fotos) ? s.hotel_fotos : []).map((p) => (X.fotosHotel && X.fotosHotel[p]) || (/^(https?:|data:)/.test(String(p)) ? p : null)).filter(Boolean).slice(0, 3), // APP 2/32
    checkin: s.checkin || '',
    checkout: s.checkout || '',
    habitaciones: (s.habitaciones != null && s.habitaciones !== '') ? String(s.habitaciones) : '',
    plan: s.plan || '',
  }));
  const vuelos = customItems.filter((s) => tipoDe(s) === 'vuelo').map((s) => ({
    nombre: s.nombre || '—',
    origen: s.origen || '',
    destino: s.destino || '',
    aerolinea: s.aerolinea || '',
    salida: s.salida || '',
    regreso: s.regreso || '',
  }));
  const servicios = [
    ...catItems.map((s) => {
      const prov = proveedores.find((p) => p.codigo === s.product.proveedor_codigo);
      return {
        nombre: nombreProd(s),
        sku: s.product.sku,
        proveedor_nombre: nombreProveedor(s.product, prov),
        destino: s.pickup || '',
        fecha: s.fecha || '',
        tipo: s.es_combo ? 'Combo' : 'Tour', // B42
      };
    }),
    ...customsGeneral.map((s) => ({
      nombre: s.nombre,
      sku: tipoDe(s) === 'transporte' ? 'TRANSPORTE' : '[CUSTOM]',
      proveedor_nombre: tipoDe(s) === 'transporte' ? [s.transporte_origen, s.transporte_destino].filter(Boolean).join(' → ') || '—' : '—',
      destino: s.pickup || '',
      fecha: s.fecha || '',
      tipo: tipoDe(s) === 'transporte' ? 'Transporte' : 'Personalizado', // B42
    })),
  ];

  // APP 2/33 (RP-12): el ticket dice el TOTAL de personas del bloque («4 personas»), no la división interna; sin reparto, el del grupo.
  const PERS = ({ es: ['persona', 'personas'], en: ['person', 'people'], pt: ['pessoa', 'pessoas'] })[idiomaDoc] || ['persona', 'personas'];
  const totalCab = (parseInt(body.adultos) || 0) + (parseInt(body.menores) || 0) + (parseInt(body.infantes) || 0);
  const totalPersonas = (s) => { const n = (s.adultos != null || s.menores != null) ? (parseInt(s.adultos) || 0) + (parseInt(s.menores) || 0) + (parseInt(s.infantes) || 0) : totalCab; return n > 0 ? `${n} ${n === 1 ? PERS[0] : PERS[1]}` : null; };
  const tickets = [
    ...catItems.map((s, i) => { const sku = s.product.sku; return { cat: X.tipos[sku] || clasificar(s.product.nombre, sku, null), nombre: nombreProd(s), sku, pickup: s.pickup || '', fecha: s.fecha || '', personas: totalPersonas(s), tags: X.etiquetas[sku] || etiquetasDe(null, s.product.incluye, idiomaDoc), ficha: X.fichas[sku] || null, foto: X.fotos[sku] || null, codigo: `${sku}-${folioNum}-${i + 1}`, estado: esCotDoc ? 'cotizado' : (['confirmado', 'enviado', 'reservado'].includes(s.estado_reserva) ? 'confirmada' : 'tentativa') }; }),
    ...customsGeneral.filter((s) => tipoDe(s) !== 'transporte').map((s, i) => ({ cat: 'tour', nombre: s.nombre || '—', sku: '', pickup: s.pickup || '', fecha: s.fecha || '', tags: [], ficha: null, foto: null, codigo: `SRV-${folioNum}-${catItems.length + i + 1}`, estado: esCotDoc ? 'cotizado' : 'tentativa' })),
  ];
  const traslados = customsGeneral.filter((s) => tipoDe(s) === 'transporte').map((s, i) => ({ nombre: s.nombre || [s.transporte_origen, s.transporte_destino].filter(Boolean).join(' → ') || 'Traslado', origen: s.transporte_origen || '', destino: s.transporte_destino || '', fecha: s.fecha || '', modo: s.transporte_tipo || s.transporte_modo || '', pax: s.transporte_pax || '', unidades: s.transporte_unidades || '', codigo: '', huella: `TRA-${folioNum}-${i + 1}`, estado: esCotDoc ? 'cotizado' : 'confirmada' }));
  const codigoHotelDe = (h, i) => { const c = h.hotel_codigo_confirmacion || (X.codigosHotel || []).find((x) => String(x.hotel_nombre || '').toLowerCase() === String(h.nombre || '').toLowerCase())?.codigo_reserva; return c ? String(c) : null; };
  const total = parseFloat(body.total) || 0;
  const deposito = parseFloat(body.deposito) || 0;
  const saldo = total - deposito;

  // ============================================================================
  // MULTI-MONEDA EN PDF (recuperado de Fase 1):
  // Lee moneda_pdf ("MXN" / "USD" / "MXN,USD" / "MXN,EUR" / etc), tasa_usd_pdf y tasa_eur_pdf
  // ============================================================================
  const monedasPdf = (body.moneda_pdf || 'MXN').split(',').map(s => s.trim()).filter(Boolean);
  const tasaUsdPdf = parseFloat(body.tasa_usd_pdf) || 17.30;
  const tasaEurPdf = parseFloat(body.tasa_eur_pdf) || 19.50;
  const convertir = (mxn, moneda) => {
    if (moneda === 'USD') return mxn / tasaUsdPdf;
    if (moneda === 'EUR') return mxn / tasaEurPdf;
    return mxn;
  };
  const bloquesMoneda = monedasPdf.map(m => ({
    moneda: m,
    total: convertir(total, m),
    deposito: convertir(deposito, m),
    saldo: convertir(saldo, m),
  }));

  const lineaCont =
    body.linea_contacto === 'OTRO' ? body.linea_contacto_otro || '—' : body.linea_contacto || '—';

  // ── Precio de oferta (SOLO decorativo del PDF de cotización, cero matemática) ──
  // Se muestra el precio regular tachado y el ahorro. Solo válido si es MAYOR al total.
  // El % se calcula únicamente para mostrar. PDFCliente lo pinta solo cuando tipo==='cotizacion'.
  const precioRegularOferta = parseFloat(body.precio_regular_oferta) || 0;
  const oferta = (precioRegularOferta > total && total > 0) ? {
    precio_regular: precioRegularOferta,
    precio_final: total,
    ahorro: precioRegularOferta - total,
    porcentaje: Math.round(((precioRegularOferta - total) / precioRegularOferta) * 100),
    moneda: 'MXN',
  } : null;

  return {
    folio,
    // Bloque 26: teléfonos por canal (Top Tours → WhatsApp "Aventuras con Pao"; Sala y Admin → el oficial).
    idioma: ['es', 'en', 'pt'].includes(body.idioma_cliente) ? body.idioma_cliente : 'es', // B50
    contacto: contactoPorCanal(body.es_top_tours ? 'top_tours' : body.canal),
    fecha_creacion: fmtFechaCorta(body.fecha_reserva),
    oferta,
    cliente: {
      nombre: body.nombre || '—',
      hotel: body.hotel || '—',
      habitacion: body.habitacion || '—',
      komito: body.komito_conversacion_id || body.komito || '', // APP 2/10
      adultos: parseInt(body.adultos) || 0,
      menores: parseInt(body.menores) || 0,
      infantes: parseInt(body.infantes) || 0,
      telefono: body.telefono_cliente || '',
      email: body.email_cliente || '',
    },
    servicios,
    hospedaje: hospedaje.map((h, i) => ({ ...h, codigo: codigoHotelDe(h, i), huella: `HTL-${folioNum}-${i + 1}` })),
    vuelos: vuelos.map((v, i) => ({ ...v, huella: `VUE-${folioNum}-${i + 1}` })),
    tickets, traslados, qr: X.qr || null, // APP 2/30
    resumen: {
      total,
      deposito,
      saldo,
      moneda: 'MXN',
      // Fase 1 multi-moneda recuperado: array de bloques con moneda + montos convertidos
      monedas_pdf: monedasPdf,
      tasa_usd_pdf: tasaUsdPdf,
      tasa_eur_pdf: tasaEurPdf,
      bloques_moneda: bloquesMoneda,
    },
    pago: {
      metodo: sanitizeMetodoPago(body.metodo_pago),
      notas: notasPartidas.texto || '—', // APP 2/28: solo el texto del agente
      linea_contacto: lineaCont,
      pago_balance: body.pago_balance || '—',
    },
    importante, // APP 2/28: [{ nombre, nota }] (PDFCliente lo imprime solo en la reserva)
    representante: {
      nombre: body.representante || '—',
      telefono: body.telefono || '—',
    },
  };
};

// ============================================================================
// buildDataComision — construye el objeto `data` para PDFComision
// ============================================================================
export const buildDataComision = ({ folio, body, catItems, customItems, proveedores }) => {
  const tasaUsdPdf = parseFloat(body.tasa_usd_pdf) || 17.30;

  // A3.2 — Composición de cabecera (fallback cuando el item no lleva lápiz).
  const cabA = parseInt(body.adultos) || 0;
  const cabM = parseInt(body.menores) || 0;
  const cabI = parseInt(body.infantes) || 0;

  const servicios = [
    ...catItems.map((s) => {
      const prov = proveedores.find((p) => p.codigo === s.product.proveedor_codigo);
      const monedaOrig = s.product.moneda || 'MXN';
      const tasaProv = parseFloat(prov?.tasa_usd) || tasaUsdPdf;

      // pubMxn y netMxn ya vienen calculados desde App.js (los pasamos en el item)
      const pubMxn = parseFloat(s.pubMxn) || 0;
      const netoMxn = parseFloat(s.netMxn) || 0;

      const precioOrig = monedaOrig !== 'MXN' ? Math.round(pubMxn / tasaProv) : null;

      // A3.2 — Composición REAL usada para el neto de este producto (a.m.i).
      const ed = !!s.composicion_editada;
      const pa = ed ? (parseInt(s.adultos) || 0) : cabA;
      const pm = ed ? (parseInt(s.menores) || 0) : cabM;
      const pi = ed ? (parseInt(s.infantes) || 0) : cabI;

      return {
        sku: s.product.sku,
        nombre: s.product.nombre,
        proveedor_nombre: nombreProveedor(s.product, prov),
        moneda_origen: monedaOrig,
        precio_origen: precioOrig,
        tasa_cambio: monedaOrig !== 'MXN' ? tasaProv : null,
        pub_mxn: pubMxn,
        neto_mxn: netoMxn,
        pax: `${pa}.${pm}.${pi}`,
        composicion_editada: ed,
        // APP 2/38: desglose interno con edades («2 adultos · 1 menor (10) · 1 infante (3)»); el PDF del cliente nunca lleva edades.
        desglose: s.reparto_edad && !s.reparto_manual && Array.isArray(s.edades_clase) ? desgloseEdades(pa, pm, pi, s.edades_clase) : null,
        es_combo: !!s.es_combo, // A4: marca "(combo)" en la tabla de servicios
      };
    }),
    ...customItems.map((s) => ({
      sku: '[CUSTOM]',
      nombre: s.nombre,
      proveedor_nombre: '—',
      moneda_origen: 'MXN',
      precio_origen: null,
      tasa_cambio: null,
      pub_mxn: parseFloat(s.pub) || 0,
      neto_mxn: parseFloat(s.net) || 0,
      // A3.2 — Custom hereda la cabecera (hoy no lleva lápiz).
      pax: `${cabA}.${cabM}.${cabI}`,
      composicion_editada: false,
    })),
  ];

  const total = parseFloat(body.total) || 0;
  const cargoInt = parseFloat(body.cargo_pago_int) || 0;
  const costoNeto = parseFloat(body.precio_neto) || 0;
  const margen = parseFloat(body.margen) || 0;
  // Bloque 38: la comisión GUARDADA (body.comision) viaja tal cual; null solo si viene vacía
  // para que el cupón pueda caer al cálculo informativo. Nunca se recalcula aquí.
  const comisionGuardada = body.comision != null && body.comision !== '' && !isNaN(parseFloat(body.comision))
    ? parseFloat(body.comision) : null;
  const comisionAgente = comisionGuardada ?? 0;
  const gananciaAgencia = margen - comisionAgente;
  const pctAgenteGuardado = body.pct_agente_tt != null && body.pct_agente_tt !== '' && !isNaN(parseFloat(body.pct_agente_tt))
    ? parseFloat(body.pct_agente_tt) : null;
  const deposito = parseFloat(body.deposito) || 0;
  const pendiente = total - deposito;

  const fechaServicio =
    catItems[0]?.fecha || customItems[0]?.fecha || body.fecha_reserva || '';

  const lineaCont =
    body.linea_contacto === 'OTRO' ? body.linea_contacto_otro || '—' : body.linea_contacto || '—';

  // FASE 1 recuperado: info multi-moneda para uso INTERNO del PDF de comisión
  // (en PDF cliente NO se muestra TC; en este sí porque es seguimiento interno)
  const monedasPdfCom = (body.moneda_pdf || 'MXN').split(',').map(s => s.trim()).filter(Boolean);
  const tasaEurPdf = parseFloat(body.tasa_eur_pdf) || 0;

  // COMBO TOP TOURS (Xcaret) — RECUPERADO.
  // El cálculo se hace en App.js (público −20% −30% sobre productos aplica_combo)
  // y viaja en body.combo_detalles (JSON) cuando body.combo_aplicado === true.
  // Aquí solo lo parseamos para que PDFComision pueda imprimir el desglose auditable.
  // El cálculo financiero (neto/margen/comisión) NO se toca: ya viene resuelto en body.
  let combo = null;
  if (body.combo_aplicado && body.combo_detalles) {
    try {
      const c = typeof body.combo_detalles === 'string'
        ? JSON.parse(body.combo_detalles)
        : body.combo_detalles;
      if (c && c.publico_original != null) {
        combo = {
          productos: Array.isArray(c.productos) ? c.productos : [],
          publico_original: parseFloat(c.publico_original) || 0,
          despues_20: parseFloat(c.despues_20) || 0,
          despues_30: parseFloat(c.despues_30) || 0,
          ahorro: parseFloat(c.ahorro) || 0,
        };
      }
    } catch (e) {
      console.warn('[pdfUploader] combo_detalles no parseable, se omite bloque combo:', e);
      combo = null;
    }
  }

  // Bloque 4 (tt_v2): folio Top Tours → el agente ve SUS DOS escenarios (Becados 30% / 50-50 50%).
  // Nunca se imprimen la ganancia de la casa ni la comisión de la supervisora.
  const esTT = body.regla_comision === 'tt_v2';
  // Bloque 22: venta de administración (canal admin) → el PDF imprime la regla contable elegida.
  const reglaAdmin = body.canal === 'admin' && body.regla_comision ? etiquetaReglaAdmin(body.regla_comision, body.pct_agente_tt) : null;
  const tt = esTT ? {
    modalidad: body.modalidad_tt === 'premier' ? 'premier' : 'basico',
    pct: Math.round((parseFloat(body.pct_agente_tt) || (body.modalidad_tt === 'premier' ? 0.5 : 0.3)) * 100),
    comision_becados: body.comision_becados != null ? parseFloat(body.comision_becados) || 0 : Math.round(Math.max(0, margen * 0.30) * 100) / 100,
    comision_5050: body.comision_5050 != null ? parseFloat(body.comision_5050) || 0 : Math.round(Math.max(0, margen * 0.50) * 100) / 100,
  } : null;

  return {
    folio,
    fecha_creacion: fmtFechaHora(),
    // Info de monedas + TC para mostrar en PDF interno (debajo del folio)
    monedas_pdf: monedasPdfCom,
    tasa_usd_pdf: tasaUsdPdf,
    tasa_eur_pdf: tasaEurPdf,
    representante: {
      nombre: body.representante || '—',
      telefono: body.telefono || '—',
    },
    cliente: {
      nombre: body.nombre || '—',
      hotel: body.hotel || '—',
      adultos: parseInt(body.adultos) || 0,
      menores: parseInt(body.menores) || 0,
      infantes: parseInt(body.infantes) || 0,
    },
    pago: {
      metodo: sanitizeMetodoPago(body.metodo_pago),
      notas: body.notas || '—',
      linea_contacto: lineaCont,
      pago_balance: body.pago_balance || '—',
    },
    fecha_servicio: fechaServicio,
    servicios,
    // COMBO TOP TOURS (Xcaret) — null si no aplica. PDFComision lo imprime si existe.
    combo,
    desglose: {
      precio_venta: total,
      cargo_int: cargoInt,
      costo_neto: costoNeto,
      margen,
      comision_agente: comisionGuardada,
      ganancia_agencia: gananciaAgencia,
      // Bloque 38: lo guardado en la reserva, para que el cupón lo imprima sin recalcular.
      pct_agente_tt: pctAgenteGuardado,
      linea_contacto: body.linea_contacto || null,
      // Fase 5C: usar 80% si es RP venta por fuera, 40% normal (TT: % de su modalidad)
      comision_pct: tt ? tt.pct : reglaAdmin ? Math.round((margen > 0 ? comisionAgente / margen : 0) * 100) : (body.es_rp_por_fuera ? 80 : 40),
      tt,
      regla_admin: reglaAdmin,
      es_rp_por_fuera: !!body.es_rp_por_fuera,
      total,
      deposito,
      pendiente,
    },
  };
};

// ============================================================================
// generateAndUploadPDFs — orquesta generación + upload de los 2 PDFs
// ============================================================================
/**
 * Genera y sube los 2 PDFs (cliente + comisión) a Supabase Storage.
 * Para COTIZACIONES, solo se genera el PDF Cliente (no comisión).
 *
 * @returns {Promise<{ success, pathCliente?, pathComision?, error? }>}
 */
export const generateAndUploadPDFs = async ({
  folio,
  tipo,
  dataCliente,
  dataComision,
  supabase,
}) => {
  const subcarpeta = tipo === 'cotizacion' ? 'cotizaciones' : 'reservas';
  const pathCliente = `${subcarpeta}/${folio}.pdf`;
  const pathComision = `${subcarpeta}/${folio}.pdf`;

  try {
    // --- PDF Cliente (siempre) · APP 2/32c: dos versiones mientras conviven ---
    // ruta de siempre (la que mandan correo y WhatsApp) = CUPON_ENVIO; el cupón 2.0 queda además en <carpeta>-v2/<folio>.pdf
    const blobNueva = await pdf(React.createElement(PDFCliente, { data: dataCliente, tipo })).toBlob();
    // APP 2/37: la versión anterior se genera SIEMPRE (no depende de banderas ni de que se pida la nueva).
    const blobAnterior = await pdf(React.createElement(PDFClienteAnterior, { data: dataCliente, tipo })).toBlob();
    const blobCliente = CUPON_ENVIO === 'anterior' ? blobAnterior : blobNueva;
    const pathV2 = rutaV2(tipo, folio);

    const { error: errC } = await supabase.storage
      .from('pdfs-cliente')
      .upload(pathCliente, blobCliente, {
        contentType: 'application/pdf',
        upsert: true,
      });
    if (errC) throw new Error('Cliente: ' + errC.message);
    const { error: errV2 } = await supabase.storage.from(BUCKET_CLIENTE).upload(pathV2, blobNueva, { contentType: 'application/pdf', upsert: true });
    if (errV2) throw new Error('Cupón 2.0: ' + errV2.message);
    const blobs = { nueva: blobNueva, anterior: blobAnterior }; // APP 2/37: las dos siempre; el panel decide cuál es la principal (CUPON_DEFAULT)

    // --- PDF Comisión (SOLO para reservas, no cotizaciones) ---
    // Las cotizaciones no generan PDF de comisión porque aún no es venta
    // efectiva. La comisión solo se calcula cuando la cotización se convierte.
    if (tipo === 'cotizacion') {
      return { success: true, pathCliente, pathComision: null, pathV2, blobs };
    }

    const blobComision = await pdf(
      React.createElement(PDFComision, { data: dataComision })
    ).toBlob();

    const { error: errM } = await supabase.storage
      .from('pdfs-comision')
      .upload(pathComision, blobComision, {
        contentType: 'application/pdf',
        upsert: true,
      });
    if (errM) throw new Error('Comisión: ' + errM.message);

    return { success: true, pathCliente, pathComision, pathV2, blobs };
  } catch (err) {
    console.error('[pdfUploader] Error:', err);
    return { success: false, error: err.message };
  }
};

// ============================================================================
// openSupabasePDF — abre un PDF privado de Supabase Storage en nueva pestaña
// ============================================================================
// APP 2/08 (M2): pestaña abierta en el mismo clic (sin bloqueo de emergentes); si algo falla se cierra.
export const abrirPestanaVacia = () => {
  try {
    const w = window.open('', '_blank');
    if (w) {
      try { w.document.title = 'Abriendo PDF…'; w.document.body.innerHTML = '<p style="font-family:sans-serif;padding:24px;color:#555">Abriendo el PDF… Si esta pestaña se queda vacía, el folio no generó PDF; puedes cerrarla.</p>'; } catch (_) { /* nada */ }
      // Si el flujo se detiene antes del PDF (validación, error al guardar), la pestaña vacía se cierra sola a los 90 s.
      try { setTimeout(() => { try { if (w && !w.closed && /^about:blank/.test(w.location.href)) w.close(); } catch (_) { /* otra página */ } }, 90000); } catch (_) { /* nada */ }
    }
    return w;
  } catch (_) { return null; }
};
export const mostrarEnPestana = (w, url) => { if (w && !w.closed) { w.location.href = url; } else { window.open(url, '_blank', 'noopener,noreferrer'); } };
export const cerrarPestana = (w) => { try { if (w && !w.closed) w.close(); } catch (_) { /* nada */ } };
// Al TERMINAR una reserva o cotización: descarga con el nombre bonito Y además abre el PDF en pestaña nueva.
export const descargarYAbrirPDF = async (supabase, bucket, path, downloadName, pestanaPrevia = null) => {
  const pestana = pestanaPrevia && !pestanaPrevia.closed ? pestanaPrevia : abrirPestanaVacia();
  try {
    const [ver, bajar] = await Promise.all([
      supabase.storage.from(bucket).createSignedUrl(path, 3600),
      supabase.storage.from(bucket).createSignedUrl(path, 3600, downloadName ? { download: downloadName } : { download: true }),
    ]);
    if (ver.error) throw ver.error;
    mostrarEnPestana(pestana, ver.data.signedUrl);
    if (!bajar.error && bajar.data?.signedUrl) {
      const a = document.createElement('a'); a.href = bajar.data.signedUrl; a.download = downloadName || ''; a.rel = 'noopener'; a.style.display = 'none';
      document.body.appendChild(a); a.click(); setTimeout(() => a.remove(), 2000);
    }
    return true;
  } catch (err) {
    cerrarPestana(pestana);
    console.error('[pdfUploader] descargarYAbrirPDF:', err);
    return false;
  }
};
/**
 * Genera URL firmada (válida 1 hora) y abre el PDF en nueva pestaña.
 *
 * @param {object} supabase - Cliente de Supabase
 * @param {string} bucket - 'pdfs-cliente' o 'pdfs-comision'
 * @param {string} path - Path interno del archivo (ej: 'reservas/MT-1137.pdf')
 * @param {string} [downloadName] - Opcional: nombre que verá el usuario al descargar
 *                                  (ej: 'MT-1137 JUAN MELAS.pdf'). Si se omite,
 *                                  abre en preview con el nombre del path.
 */
// APP 2/08 (M2): VER = liga firmada SIN `download` → se abre en pestaña nueva (antes se descargaba y en Mac estorbaba).
// Para no chocar con el bloqueo de ventanas emergentes, la pestaña se abre en el mismo clic (antes del await) y luego
// se le asigna la URL. `downloadName` se conserva como parámetro por compatibilidad, pero ya no fuerza la descarga:
// la descarga con nombre bonito solo pasa al TERMINAR una reserva/cotización (descargarYAbrirPDF).
export const openSupabasePDF = async (supabase, bucket, path, downloadName = null) => {
  const pestana = abrirPestanaVacia();
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, 3600);
    if (error) throw error;
    mostrarEnPestana(pestana, data.signedUrl);
  } catch (err) {
    cerrarPestana(pestana);
    console.error('[pdfUploader] openSupabasePDF:', err);
    alert('Error al abrir el PDF: ' + err.message);
  }
};

// ============================================================================
// updateBDSupabasePaths — actualiza la BD con los paths de Supabase
// ============================================================================
/**
 * Después de subir los PDFs a Supabase, registra los paths en la BD para que
 * los listados puedan encontrarlos. Si falla, se ignora el error (los PDFs
 * de Drive siguen funcionando como respaldo).
 *
 * @param {object} supabase - Cliente de Supabase
 * @param {'reserva'|'cotizacion'} tipo
 * @param {string} folio - ej: 'MT-1137' o 'MTC-1108'
 * @param {string} pathCliente - ej: 'reservas/MT-1137.pdf'
 * @param {string} pathComision - ej: 'reservas/MT-1137.pdf'
 */
export const updateBDSupabasePaths = async (supabase, tipo, folio, pathCliente, pathComision) => {
  try {
    const tabla = tipo === 'cotizacion' ? 'cotizaciones' : 'reservas';
    const { error } = await supabase
      .from(tabla)
      .update({
        pdf_supabase_cliente: pathCliente,
        pdf_supabase_comision: pathComision,
      })
      .eq('folio', folio);
    if (error) throw error;
    return { success: true };
  } catch (err) {
    console.error('[pdfUploader] updateBDSupabasePaths:', err);
    return { success: false, error: err.message };
  }
};

// ============================================================================
// openPDFInteligente — abre un PDF eligiendo automáticamente Supabase o Drive
// ============================================================================
/**
 * Lógica de fallback automático: si la reserva tiene path de Supabase, abre
 * desde ahí (más nuevo, mejor calidad). Si no tiene, cae al pdf_url de Drive
 * (reservas históricas).
 *
 * @param {object} supabase - Cliente de Supabase
 * @param {object} reserva - Objeto con pdf_url, pdf_comision_url,
 *                           pdf_supabase_cliente, pdf_supabase_comision, folio, nombre
 * @param {'cliente'|'comision'} type
 */
export const openPDFInteligente = (supabase, reserva, type) => {
  const pathSupabase = type === 'cliente'
    ? reserva.pdf_supabase_cliente
    : reserva.pdf_supabase_comision;
  const urlDrive = type === 'cliente'
    ? reserva.pdf_url
    : reserva.pdf_comision_url;

  if (pathSupabase) {
    const bucket = type === 'cliente' ? 'pdfs-cliente' : 'pdfs-comision';
    const sufijo = type === 'comision' ? ' COMISIONES' : '';
    // Fix nombre PDF: incluir todos los campos posibles donde puede estar el nombre del cliente
    // (reserva.nombre = tabla reservas / cotizaciones, reserva.cliente = vista cupones_admin y ventas)
    const cliente = reserva.nombre || reserva.cliente_nombre || reserva.cliente || '';
    const downloadName = safeFileName(`${reserva.folio}${sufijo}${cliente ? ' ' + cliente : ''}`) + '.pdf';
    openSupabasePDF(supabase, bucket, pathSupabase, downloadName);
  } else if (urlDrive) {
    window.open(urlDrive, '_blank', 'noopener,noreferrer');
  } else {
    console.warn('[pdfUploader] openPDFInteligente: no hay ni Supabase ni Drive para', reserva.folio);
  }
};

// ============================================================================
// FASE 5B — CUPONES DE AJUSTE DE BALANCE
// ============================================================================

// ─── Helper: formatear fecha 'YYYY-MM-DD' → 'DD-MM-YYYY' ────────────────
const fmtFechaCortaPagos = (fecha) => {
  if (!fecha) return '';
  if (typeof fecha === 'string') {
    // Si viene en formato ISO o YYYY-MM-DD
    const m = fecha.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  }
  return fecha;
};

/**
 * Construye el objeto `data` para el PDF Cupón de Ajuste (tipo='ajuste').
 *
 * @param {object} args
 * @param {object} args.reserva       - registro completo de la tabla 'reservas'
 *                                      (debe traer nombre, hotel, habitacion,
 *                                      adultos, menores, infantes, total,
 *                                      deposito, fecha_reserva, metodo_pago,
 *                                      notas, linea_contacto, pago_balance, etc.)
 * @param {Array}  args.servicios     - servicios contratados (de la reserva)
 * @param {Array}  args.pagosPrevios  - registros de pagos_cliente DE ESTA RESERVA,
 *                                      ordenados ascendentemente (incluye el nuevo)
 * @param {object} args.agente        - { nombre, telefono } del agente dueño
 * @returns {object} data para PDFCliente con tipo='ajuste'
 */
export const buildDataCuponAjuste = ({ reserva, servicios, pagosPrevios, agente }) => {
  const total = parseFloat(reserva.total) || 0;
  const depositoInicial = parseFloat(reserva.deposito) || 0;

  // Construir histórico ordenado: Depósito inicial + Pago 1, 2, 3...
  const historial = [
    {
      etiqueta: 'Depósito inicial',
      monto: depositoInicial,
      fecha: fmtFechaCortaPagos(reserva.fecha_reserva),
    },
    ...pagosPrevios.map((p, i) => ({
      etiqueta: `Pago ${i + 1}`,
      monto: parseFloat(p.monto) || 0,
      fecha: fmtFechaCortaPagos(p.created_at),
    })),
  ];

  // Saldo actual = total - depósito inicial - suma de todos los pagos
  const totalPagado = depositoInicial + pagosPrevios.reduce((s, p) => s + (parseFloat(p.monto) || 0), 0);
  const saldoActual = total - totalPagado;

  // A2.3f — Multi-moneda: si la reserva trae moneda_pdf con USD/EUR y sus tasas, calcular
  // equivalentes (total/depósito/saldo) como el PDF cliente normal. Si no hay datos, solo MXN.
  const monedasPdf = (reserva.moneda_pdf || 'MXN').split(',').map((s) => s.trim()).filter(Boolean);
  const tasaUsdPdf = parseFloat(reserva.tasa_usd_pdf) || 17.30;
  const tasaEurPdf = parseFloat(reserva.tasa_eur_pdf) || 19.50;
  const convertirMoneda = (mxn, m) => (m === 'USD' ? mxn / tasaUsdPdf : m === 'EUR' ? mxn / tasaEurPdf : mxn);
  const bloquesMonedaAjuste = (monedasPdf.length ? monedasPdf : ['MXN']).map((m) => ({
    moneda: m,
    total: convertirMoneda(total, m),
    deposito: convertirMoneda(depositoInicial, m),
    saldo: convertirMoneda(saldoActual, m),
  }));

  // El folio del cupón es MT-XXXX-N donde N = número del último pago
  const numeroPago = pagosPrevios.length; // 1, 2, 3...
  const folioCupon = `${reserva.folio}-${numeroPago}`;

  // Fecha del cupón = fecha de hoy formateada
  const hoy = new Date();
  const dd = String(hoy.getDate()).padStart(2, '0');
  const mm = String(hoy.getMonth() + 1).padStart(2, '0');
  const yyyy = hoy.getFullYear();
  const fechaCreacion = `${dd}-${mm}-${yyyy}`;

  // Línea de contacto: si era 'OTRO', usar el campo libre
  const lineaCont = reserva.linea_contacto === 'OTRO'
    ? (reserva.linea_contacto_otro || '—')
    : (reserva.linea_contacto || '—');

  return {
    folio: folioCupon,
    fecha_creacion: fechaCreacion,
    cliente: {
      nombre: reserva.nombre || '—',
      hotel: reserva.hotel || '—',
      habitacion: reserva.habitacion || '—',
      adultos: parseInt(reserva.adultos) || 0,
      menores: parseInt(reserva.menores) || 0,
      infantes: parseInt(reserva.infantes) || 0,
      telefono: reserva.telefono_cliente || '',
      email: reserva.email_cliente || '',
    },
    servicios: servicios || [],
    resumen: {
      total,
      historial,
      saldo: saldoActual,
      moneda: 'MXN',
      // A2.3f: equivalentes multi-moneda (Total/Depósito/Saldo) para el estado de cuenta.
      monedas_pdf: monedasPdf,
      tasa_usd_pdf: tasaUsdPdf,
      tasa_eur_pdf: tasaEurPdf,
      bloques_moneda: bloquesMonedaAjuste,
    },
    pago: {
      metodo: sanitizeMetodoPago(reserva.metodo_pago),
      notas: partirNotas(reserva.notas).texto || '—', // APP 2/28
      linea_contacto: lineaCont,
      pago_balance: reserva.pago_balance || '—',
    },
    importante: partirNotas(reserva.notas).importante.map((x) => ({ nombre: sinEmoji(x.nombre), nota: sinEmoji(x.nota) })), // APP 2/28
    representante: {
      nombre: agente?.nombre || '—',
      telefono: agente?.telefono || '—',
    },
  };
};

/**
 * Genera el PDF de Cupón de Ajuste y lo sube a Supabase Storage.
 * Path resultante: 'reservas/{folio}-{numeroPago}.pdf'
 *
 * @returns {Promise<{ success, path?, error? }>}
 */
// ============================================================================
// C1 — generateAndUploadCambio — genera y sube el Cupón de Cambio (simulación).
// Path: 'cambios/{folioCambio}.pdf' en el bucket 'pdfs-comision'.
// ============================================================================
// ============================================================================
// P1 — generateAndUploadPickups — PDF de confirmación de reserva (pickups).
// Path: 'confirmaciones/{folio}.pdf' en el bucket 'pdfs-cliente'.
// ============================================================================
export const generateAndUploadPickups = async ({ data, folio, supabase }) => {
  try {
    const blob = await pdf(React.createElement(PDFPickups, { data })).toBlob();
    const path = `confirmaciones/${folio}.pdf`;
    const { error } = await supabase.storage
      .from('pdfs-cliente')
      .upload(path, blob, { contentType: 'application/pdf', upsert: true });
    if (error) throw new Error(error.message);
    return { success: true, path };
  } catch (err) {
    console.error('[pdfUploader] generateAndUploadPickups:', err);
    return { success: false, error: err.message };
  }
};

export const generateAndUploadCambio = async ({ data, folioCambio, supabase }) => {
  try {
    const blob = await pdf(React.createElement(PDFCambio, { data })).toBlob();
    const path = `cambios/${folioCambio}.pdf`;
    const { error } = await supabase.storage
      .from('pdfs-comision')
      .upload(path, blob, { contentType: 'application/pdf', upsert: true });
    if (error) throw new Error(error.message);
    return { success: true, path };
  } catch (err) {
    console.error('[pdfUploader] generateAndUploadCambio:', err);
    return { success: false, error: err.message };
  }
};

export const generateAndUploadCuponAjuste = async ({ data, folio, numeroPago, supabase }) => {
  const path = `reservas/${folio}-${numeroPago}.pdf`;

  try {
    const blob = await pdf(
      React.createElement(PDFEnvio, { data, tipo: 'ajuste' }) // APP 2/32c: la versión que va al cliente
    ).toBlob();

    const { error } = await supabase.storage
      .from('pdfs-cliente')
      .upload(path, blob, {
        contentType: 'application/pdf',
        upsert: true,
      });
    if (error) throw new Error(error.message);

    return { success: true, path };
  } catch (err) {
    console.error('[pdfUploader] generateAndUploadCuponAjuste:', err);
    return { success: false, error: err.message };
  }
};

// ============================================================================
// safeFileName — sanitiza un string para usarlo como nombre de archivo
// ============================================================================
/**
 * Quita caracteres prohibidos en nombres de archivo (Windows/Mac/Linux):
 *   < > : " / \ | ? *  y caracteres de control.
 * Reemplaza múltiples espacios por uno solo.
 */
export const safeFileName = (str) => {
  return String(str || '')
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

// ============================================================================
// Bloque 4 — REGENERAR PDF DE COMISIÓN desde la reserva guardada
// ============================================================================
/**
 * Reconstruye el PDF de comisión con los valores ACTUALES de la fila de
 * `reservas` (margen, comision_agente, regla_comision, modalidad_tt…) y su
 * `servicios_detalle`, y lo sube encima del anterior (upsert) en pdfs-comision.
 * Sirve para folios recalculados por SQL (p. ej. ST-1101 con la regla tt_v2).
 * No toca montos en la base: solo el archivo PDF (y el path si no existía).
 */
// ── Contabilidad v3 (B50): nombres de productos por idioma (meta_title_en / meta_title_pt de productos_web_meta) ──
export const nombresPorIdioma = async (supabase, skus, idioma) => {
  const lista = [...new Set((skus || []).filter(Boolean))];
  if (!lista.length || !['en', 'pt'].includes(idioma)) return {};
  try {
    const col = idioma === 'en' ? 'meta_title_en' : 'meta_title_pt';
    const { data } = await supabase.from('productos_web_meta').select(`sku, ${col}`).in('sku', lista);
    const out = {}; (data || []).forEach((r) => { if (r[col]) out[r.sku] = r[col]; });
    return out;
  } catch (e) { return {}; }
};
// Abre el cupón del cliente en otro idioma, reconstruido desde la reserva guardada (no se sube ni reemplaza el PDF original).
// APP 2/37: en inglés / portugués sale la versión DEFAULT (anterior); version: 'nueva' para probar el 2.0.
export const abrirPDFClienteIdioma = async ({ supabase, reservaId, idioma = 'en', proveedores = [], version = CUPON_DEFAULT }) => {
  const { data: reserva, error } = await supabase.from('reservas').select('*').eq('id', reservaId).maybeSingle();
  if (error || !reserva) throw new Error(error?.message || 'Folio no encontrado');
  const det = Array.isArray(reserva.servicios_detalle) ? reserva.servicios_detalle : [];
  const vivos = det.filter((d) => !['cancelado', 'reemplazado'].includes(d.estado_reserva));
  const catItems = vivos.filter((d) => !d.es_custom).map((d) => ({ product: { sku: d.sku, nombre: d.nombre, proveedor_codigo: d.proveedor, moneda: d.moneda || 'MXN' }, pubMxn: Number(d.subtotal_publico) || 0, netMxn: Number(d.subtotal_neto) || 0, es_combo: !!d.es_combo, fecha: d.fecha || d.fecha_actividad || '', pickup: d.hotel || '' }));
  const customItems = vivos.filter((d) => d.es_custom).map((d) => ({ nombre: d.nombre, pub: Number(d.subtotal_publico) || 0, net: Number(d.subtotal_neto) || 0, fecha: d.fecha || d.fecha_actividad || '', pickup: d.hotel || '', tipo_custom: d.tipo_custom || 'general', checkin: d.hotel_checkin || '', checkout: d.hotel_checkout || '', habitaciones: d.hotel_habitaciones || '', plan: d.hotel_plan || '', origen: d.vuelo_origen || '', destino: d.vuelo_destino || '', aerolinea: d.vuelo_aerolinea || '', salida: d.vuelo_salida || '', regreso: d.vuelo_regreso || '', transporte_origen: d.transporte_origen || '', transporte_destino: d.transporte_destino || '', transporte_tipo: d.transporte_tipo || '', transporte_modo: d.transporte_modo || '', transporte_pax: d.transporte_pax || '', transporte_unidades: d.transporte_unidades || '', hotel_codigo_confirmacion: d.hotel_codigo_confirmacion || '', hotel_fotos: Array.isArray(d.hotel_fotos) ? d.hotel_fotos : [] }));
  const nombresIdioma = await nombresPorIdioma(supabase, catItems.map((c) => c.product.sku), idioma);
  const extras = await extrasCupon(supabase, { skus: catItems.map((c) => c.product.sku), reservaId: reserva.id, folio: reserva.folio || reserva.folio_personalizado, tipo: reserva.tipo === 'cotizacion' ? 'cotizacion' : 'reserva', idioma, rutasFotosHotel: customItems.flatMap((c) => c.hotel_fotos || []) }); // APP 2/30 · 2/32
  const body = { ...reserva, idioma_cliente: idioma, deposito: reserva.deposito, total: reserva.total, moneda_pdf: reserva.moneda_pdf || 'MXN', tasa_usd_pdf: reserva.tasa_usd_pdf || reserva.tasa_cambio_usada, tasa_eur_pdf: reserva.tasa_eur_pdf, email_cliente: reserva.email_cliente, telefono_cliente: reserva.telefono_cliente };
  const data = buildDataCliente({ folio: reserva.folio || reserva.folio_personalizado, body, catItems, customItems, proveedores, nombresIdioma, extras });
  const blob = await pdf(React.createElement(version === 'nueva' ? PDFCliente : PDFClienteAnterior, { data, tipo: reserva.tipo === 'cotizacion' ? 'cotizacion' : 'reserva' })).toBlob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
};
export const regenerarPDFComisionDesdeReserva = async ({ supabase, reserva, proveedores = [] }) => {
  try {
    const det = Array.isArray(reserva.servicios_detalle) ? reserva.servicios_detalle : [];
    if (!det.length) throw new Error('El folio no tiene servicios_detalle guardado; no se puede reconstruir.');
    const catItems = det.filter((d) => !d.es_custom).map((d) => ({
      product: { sku: d.sku, nombre: d.nombre, proveedor_codigo: d.proveedor, moneda: d.moneda || 'MXN',
        ...(d.es_temporal ? { es_temporal: true, proveedor_nombre_tt: d.proveedor_nombre || null } : {}) },
      pubMxn: Number(d.subtotal_publico) || 0,
      netMxn: Number(d.subtotal_neto) || 0,
      composicion_editada: !!d.composicion_editada,
      adultos: d.adultos, menores: d.menores, infantes: d.infantes,
      es_combo: !!d.es_combo,
      reparto_edad: !!d.reparto_edad, reparto_manual: !!d.reparto_manual, edades_clase: d.edades_clase || null, // APP 2/38: desglose interno
      fecha: d.fecha || '',
    }));
    const customItems = det.filter((d) => d.es_custom).map((d) => ({
      nombre: d.nombre, pub: Number(d.subtotal_publico) || 0, net: Number(d.subtotal_neto) || 0, fecha: d.fecha || d.fecha_actividad || '',
      tipo_custom: d.tipo_custom || 'general', transporte_origen: d.transporte_origen || '', transporte_destino: d.transporte_destino || '', // B42
    }));
    const total = Number(reserva.total) || 0;
    const esTT = reserva.regla_comision === 'tt_v2';
    const efectivo = String(reserva.metodo_pago || '').toLowerCase().includes('efectivo');
    const margen = Number(reserva.margen) || 0;
    const body = {
      representante: reserva.representante, telefono: reserva.telefono,
      nombre: reserva.nombre, hotel: reserva.hotel,
      adultos: reserva.adultos, menores: reserva.menores, infantes: reserva.infantes,
      total, deposito: reserva.deposito,
      // TT: el 5% no entra al margen (no se imprime). Sala: se deriva del total como en el alta.
      cargo_pago_int: esTT || efectivo ? 0 : total - total / 1.05,
      precio_neto: reserva.precio_neto, margen, comision: reserva.comision_agente,
      metodo_pago: reserva.metodo_pago, notas: reserva.notas,
      linea_contacto: reserva.linea_contacto, linea_contacto_otro: reserva.linea_contacto_otro,
      pago_balance: reserva.pago_balance, fecha_reserva: reserva.fecha_reserva,
      moneda_pdf: reserva.moneda_pdf || 'MXN', tasa_usd_pdf: reserva.tasa_usd_pdf || reserva.tasa_cambio_usada, tasa_eur_pdf: reserva.tasa_eur_pdf,
      combo_aplicado: reserva.combo_aplicado, combo_detalles: reserva.combo_detalles,
      es_rp_por_fuera: reserva.es_rp_por_fuera,
      regla_comision: reserva.regla_comision, modalidad_tt: reserva.modalidad_tt, pct_agente_tt: reserva.pct_agente_tt,
      canal: reserva.canal, vendido_por_admin_id: reserva.vendido_por_admin_id,
      comision_becados: esTT ? Math.round(Math.max(0, margen * 0.30) * 100) / 100 : undefined,
      comision_5050: esTT ? Math.round(Math.max(0, margen * 0.50) * 100) / 100 : undefined,
    };
    const folio = reserva.folio || reserva.folio_personalizado;
    const data = buildDataComision({ folio, body, catItems, customItems, proveedores });
    const blob = await pdf(React.createElement(PDFComision, { data })).toBlob();
    const path = reserva.pdf_supabase_comision || `reservas/${folio}.pdf`;
    const { error: upErr } = await supabase.storage.from('pdfs-comision').upload(path, blob, { contentType: 'application/pdf', upsert: true });
    if (upErr) throw new Error(upErr.message);
    if (!reserva.pdf_supabase_comision && reserva.id != null) {
      const { error: uErr } = await supabase.from('reservas').update({ pdf_supabase_comision: path }).eq('id', reserva.id);
      if (uErr) throw new Error('PDF subido, pero no se pudo guardar su ruta: ' + uErr.message);
    }
    return { success: true, path };
  } catch (err) {
    console.error('[pdfUploader] regenerarPDFComisionDesdeReserva:', err);
    return { success: false, error: err.message || String(err) };
  }
};

export { extrasCupon }; // APP 2/30: el cupón 2.0 pide sus extras desde App.js

// APP 2/32c: crea (o rehace) el cupón 2.0 de un folio ya guardado y lo deja en <carpeta>-v2/<folio>.pdf. Devuelve la ruta.
export const generarV2DesdeReserva = async ({ supabase, reservaId, proveedores = [] }) => {
  const { data: reserva, error } = await supabase.from('reservas').select('*').eq('id', reservaId).maybeSingle();
  if (error || !reserva) throw new Error(error?.message || 'Folio no encontrado');
  const idioma = ['es', 'en', 'pt'].includes(reserva.idioma_cliente) ? reserva.idioma_cliente : 'es';
  const det = Array.isArray(reserva.servicios_detalle) ? reserva.servicios_detalle : [];
  const vivos = det.filter((d) => !['cancelado', 'reemplazado'].includes(d.estado_reserva));
  const catItems = vivos.filter((d) => !d.es_custom).map((d) => ({ product: { sku: d.sku, nombre: d.nombre, proveedor_codigo: d.proveedor, moneda: d.moneda || 'MXN' }, pubMxn: Number(d.subtotal_publico) || 0, netMxn: Number(d.subtotal_neto) || 0, es_combo: !!d.es_combo, fecha: d.fecha || d.fecha_actividad || '', pickup: d.hotel || '', adultos: d.adultos, menores: d.menores, infantes: d.infantes, estado_reserva: d.estado_reserva }));
  const customItems = vivos.filter((d) => d.es_custom).map((d) => ({ nombre: d.nombre, pub: Number(d.subtotal_publico) || 0, net: Number(d.subtotal_neto) || 0, fecha: d.fecha || d.fecha_actividad || '', pickup: d.hotel || '', tipo_custom: d.tipo_custom || 'general', checkin: d.hotel_checkin || '', checkout: d.hotel_checkout || '', habitaciones: d.hotel_habitaciones || '', plan: d.hotel_plan || '', origen: d.vuelo_origen || '', destino: d.vuelo_destino || '', aerolinea: d.vuelo_aerolinea || '', salida: d.vuelo_salida || '', regreso: d.vuelo_regreso || '', transporte_origen: d.transporte_origen || '', transporte_destino: d.transporte_destino || '', transporte_tipo: d.transporte_tipo || '', transporte_pax: d.transporte_pax || '', transporte_unidades: d.transporte_unidades || '', hotel_codigo_confirmacion: d.hotel_codigo_confirmacion || '', hotel_fotos: Array.isArray(d.hotel_fotos) ? d.hotel_fotos : [] }));
  const tipo = reserva.tipo === 'cotizacion' ? 'cotizacion' : 'reserva';
  const folio = reserva.folio || reserva.folio_personalizado;
  const nombresIdioma = await nombresPorIdioma(supabase, catItems.map((c) => c.product.sku), idioma);
  const extras = await extrasCupon(supabase, { skus: catItems.map((c) => c.product.sku), reservaId: reserva.id, folio, tipo, idioma, rutasFotosHotel: customItems.flatMap((c) => c.hotel_fotos || []) });
  const body = { ...reserva, idioma_cliente: idioma, tipo_documento: tipo };
  const data = buildDataCliente({ folio, body, catItems, customItems, proveedores, nombresIdioma, extras });
  const blob = await pdf(React.createElement(PDFCliente, { data, tipo })).toBlob();
  const ruta = rutaV2(tipo, folio);
  const { error: e2 } = await supabase.storage.from(BUCKET_CLIENTE).upload(ruta, blob, { contentType: 'application/pdf', upsert: true });
  if (e2) throw new Error(e2.message);
  return ruta;
};
